/**
 * Contenido que sostiene la confianza: diferenciadores, proceso,
 * testimonios y preguntas frecuentes.
 */

/* ---------------- ¿Por qué elegirnos? ---------------- */
export const DIFFERENTIATORS = [
  {
    icon: "layers",
    title: {
      es: "Trabajamos con múltiples aseguradoras",
      en: "We work with multiple insurance carriers",
    },
    text: {
      es: "No representamos a una sola compañía, eso nos deja libres para recomendar lo que realmente te conviene.",
      en: "We do not represent a single company, which leaves us free to recommend what truly works best for you.",
    },
  },
  {
    icon: "user",
    title: {
      es: "Asesor personalizado",
      en: "Personalized advisor",
    },
    text: {
      es: "Un solo asesor te acompañará de principio a fin para ofrecerte una experiencia clara y consistente.",
      en: "One advisor will guide you from start to finish to provide a clear and consistent experience.",
    },
  },
  {
    icon: "heart",
    title: {
      es: "Queremos ayudarte, no venderte",
      en: "We want to help you, not sell to you",
    },
    text: {
      es: "Cada familia es diferente, por esto nos interesa conocer tu necesidad antes de ofrecerte cualquier solución.",
      en: "Every family is different, which is why we want to understand your needs before offering any solution.",
    },
  },
  {
    icon: "shield",
    title: {
      es: "Te acompañamos en la reclamación",
      en: "We stand with you at claim time",
    },
    text: {
      es: "Radicamos la reclamación contigo, damos seguimiento y te acompañamos durante todo el proceso hasta su resolución.",
      en: "We file the claim with you, follow up, and support you throughout the entire process until it is resolved.",
    },
  },
];

/* ---------------- Nuestro proceso ---------------- */
export const PROCESS = [
  {
    title: { es: "Conversación inicial", en: "Initial conversation" },
    text: {
      es: "20 minutos por teléfono, videollamada o en nuestra oficina en Quebradillas. Escuchamos tu situación: familia, ingresos, deudas y lo que te preocupa.",
      en: "20 minutes by phone, video call or at our Quebradillas office. We listen to your situation: family, income, debt and what worries you.",
    },
    meta: { es: "Sin costo · Sin compromiso", en: "Free · No commitment" },
  },
  {
    title: { es: "Análisis de necesidades", en: "Needs analysis" },
    text: {
      es: "Calculamos tu exposición real y revisamos lo que ya tienes. Muchas veces el primer hallazgo es que ya pagas por algo duplicado.",
      en: "We calculate your real exposure and review what you already have. Often the first finding is that you are paying for duplicated coverage.",
    },
    meta: { es: "2 a 3 días hábiles", en: "2 to 3 business days" },
  },
  {
    title: { es: "Propuesta comparada", en: "Compared proposal" },
    text: {
      es: "Te presentamos opciones de distintas aseguradoras lado a lado, con la prima, lo que cubre y — sobre todo — lo que no cubre cada una.",
      en: "We present options from different carriers side by side, with premium, what each covers and — above all — what each does not.",
    },
    meta: { es: "Documento por escrito", en: "Written document" },
  },
  {
    title: { es: "Emisión y acompañamiento", en: "Issue and ongoing support" },
    text: {
      es: "Gestionamos la solicitud, te entregamos la póliza explicada y revisamos contigo cada año. Si hay reclamación, la radicamos juntos.",
      en: "We manage the application, hand you the policy explained, and review it with you every year. If there is a claim, we file it together.",
    },
    meta: { es: "Revisión anual incluida", en: "Annual review included" },
  },
];

/* ---------------- Testimonios ---------------- */
export const TESTIMONIALS = [
  {
    id: "t1",
    name: "María C.",
    role: { es: "Maestra · Caguas", en: "Teacher · Caguas" },
    rating: 5,
    quote: {
      es: "Llegué pensando que me iban a vender algo caro. Salí con un plan más pequeño del que pensaba comprar, porque me explicaron que ya tenía parte cubierta por el patrono. Eso me ganó como cliente.",
      en: "I arrived expecting an expensive sale. I left with a smaller plan than I intended to buy, because they explained I was already partly covered through my employer. That won me over.",
    },
  },
  {
    id: "t2",
    name: "Roberto & Ana",
    role: { es: "Comerciantes · Bayamón", en: "Business owners · Bayamón" },
    rating: 5,
    quote: {
      es: "Cuando a Ana le diagnosticaron, el cheque del plan de cáncer llegó antes de la primera quimio. No tuvimos que tocar los ahorros del negocio ni un solo día.",
      en: "When Ana was diagnosed, the cancer plan check arrived before the first chemo. We never had to touch the business savings, not for a single day.",
    },
  },
  {
    id: "t3",
    name: "Luis M.",
    role: { es: "Ingeniero · San Juan", en: "Engineer · San Juan" },
    rating: 5,
    quote: {
      es: "Tenía tres pólizas de tres agentes distintos y ninguno me contestaba. Aquí consolidé todo con una sola persona y ahora sé exactamente qué tengo y qué me falta.",
      en: "I had three policies from three different agents and none of them answered. Here I consolidated everything with one person and now I know exactly what I have and what I am missing.",
    },
  },
  {
    id: "t4",
    name: "Carmen R.",
    role: { es: "Retirada · Ponce", en: "Retired · Ponce" },
    rating: 5,
    quote: {
      es: "Mis hijos no tuvieron que poner un centavo para el funeral de mi esposo. Eso fue lo que él quiso y por eso lo hicimos con tiempo.",
      en: "My children did not have to put up a cent for my husband's funeral. That is what he wanted, and that is why we did it in time.",
    },
  },
];

/* ---------------- Preguntas frecuentes ---------------- */
export const FAQS = [
  {
    id: "costo-orientacion",
    q: {
      es: "¿La orientación tiene algún costo?",
      en: "Is there any cost for the consultation?",
    },
    a: {
      es: "No. La orientación es gratuita y sin compromiso, presencial en nuestra oficina de Quebradillas, por teléfono o por videollamada. Nuestra compensación proviene de las aseguradoras cuando decides adquirir una póliza, nunca de ti directamente.",
      en: "No. The consultation is free and without obligation, in person at our Quebradillas office, by phone or by video call. Our compensation comes from the carriers when you decide to purchase a policy, never directly from you.",
    },
  },
  {
    id: "diferencia-agencia",
    q: {
      es: "¿Cuál es la diferencia entre una agencia y una aseguradora?",
      en: "What is the difference between an agency and a carrier?",
    },
    a: {
      es: "La aseguradora emite y paga la póliza. La agencia te representa a ti: compara opciones entre varias aseguradoras, te explica las diferencias y te acompaña durante la vida de la póliza y en la reclamación. Nosotros trabajamos con más de ocho aseguradoras, así que no estamos atados a un solo catálogo.",
      en: "The carrier issues and pays the policy. The agency represents you: it compares options across several carriers, explains the differences and stands with you through the life of the policy and at claim time. We work with more than eight carriers, so we are not tied to a single catalog.",
    },
  },
  {
    id: "ya-tengo-plan-medico",
    q: {
      es: "Ya tengo plan médico. ¿Para qué necesito un plan suplementario?",
      en: "I already have a health plan. Why would I need a supplemental plan?",
    },
    a: {
      es: "Tu plan médico le paga al hospital y al médico. Un plan suplementario te paga a ti en efectivo, sin restricción de uso, para cubrir lo que el plan médico no contempla: transportación, hospedaje, deducibles, coaseguros, cuidado en casa y el ingreso que dejas de generar durante el tratamiento.",
      en: "Your health plan pays the hospital and the doctor. A supplemental plan pays you in cash, with no usage restriction, to cover what the health plan does not: transportation, lodging, deductibles, coinsurance, home care and the income you stop earning during treatment.",
    },
  },
  {
    id: "cambiar-agencia",
    q: {
      es: "Tengo pólizas con otra agencia. ¿Puedo cambiarme sin perderlas?",
      en: "I have policies with another agency. Can I switch without losing them?",
    },
    a: {
      es: "Sí. En la mayoría de los casos puedes transferir el servicio de tus pólizas vigentes sin cancelarlas ni volver a pasar por suscripción. Antes de recomendar cualquier cambio auditamos lo que ya tienes: si tu póliza actual es mejor que lo que podemos ofrecerte, te lo decimos y la dejas como está.",
      en: "Yes. In most cases you can transfer servicing of your active policies without cancelling or re-underwriting them. Before recommending any change we audit what you already have: if your current policy is better than what we can offer, we tell you and you keep it.",
    },
  },
  {
    id: "condicion-preexistente",
    q: {
      es: "Tengo una condición preexistente. ¿Puedo asegurarme?",
      en: "I have a pre-existing condition. Can I still get coverage?",
    },
    a: {
      es: "En muchos casos sí, aunque puede aplicar un período de espera, una prima ajustada o una exclusión específica de esa condición. Existen además productos de emisión simplificada y garantizada. Lo importante es declarar tu historial con exactitud: una omisión es la causa más común de denegación al momento de reclamar.",
      en: "In many cases yes, though a waiting period, an adjusted premium or a specific exclusion for that condition may apply. There are also simplified and guaranteed issue products. What matters is disclosing your history accurately: an omission is the most common cause of denial at claim time.",
    },
  },
  {
    id: "tiempo-emision",
    q: {
      es: "¿Cuánto tarda en emitirse una póliza?",
      en: "How long does it take to issue a policy?",
    },
    a: {
      es: "Depende del producto. Los planes de emisión simplificada pueden quedar activos en cuestión de días. Los que requieren evaluación médica o financiera suelen tomar entre dos y cuatro semanas. Te damos una fecha estimada al momento de radicar la solicitud y te avisamos en cada etapa.",
      en: "It depends on the product. Simplified issue plans can be active within days. Those requiring medical or financial underwriting typically take two to four weeks. We give you an estimated date when the application is filed and update you at every stage.",
    },
  },
  {
    id: "reclamacion-ayuda",
    q: {
      es: "Si tengo una reclamación, ¿ustedes me ayudan?",
      en: "If I have a claim, do you help me?",
    },
    a: {
      es: "Sí, y es donde más valor aportamos. Te indicamos qué documentos reunir, revisamos los formularios antes de someterlos, damos seguimiento a la aseguradora y, si la reclamación se deniega de forma improcedente, preparamos la reconsideración contigo.",
      en: "Yes, and it is where we add the most value. We tell you which documents to gather, review the forms before submission, follow up with the carrier and, if a claim is improperly denied, we prepare the reconsideration with you.",
    },
  },
  {
    id: "dejar-de-pagar",
    q: {
      es: "¿Qué pasa si dejo de pagar una prima?",
      en: "What happens if I miss a premium payment?",
    },
    a: {
      es: "Casi todas las pólizas tienen un período de gracia (comúnmente 30 días) durante el cual la cubierta sigue activa. Pasado ese término la póliza puede caducar, y reactivarla puede requerir nueva evaluación. Si prevés un mes difícil, avísanos antes: casi siempre hay alternativas.",
      en: "Almost every policy has a grace period (commonly 30 days) during which coverage stays active. After that the policy may lapse, and reinstating it can require new underwriting. If you anticipate a tough month, tell us beforehand: there is almost always an alternative.",
    },
  },
  {
    id: "datos-privacidad",
    q: {
      es: "¿Qué hacen con mi información personal?",
      en: "What do you do with my personal information?",
    },
    a: {
      es: "La usamos exclusivamente para preparar tu orientación y, si decides avanzar, para tramitar la solicitud con la aseguradora. No vendemos ni compartimos tus datos con terceros ajenos a ese proceso. Puedes pedirnos en cualquier momento que dejemos de contactarte.",
      en: "We use it exclusively to prepare your consultation and, if you move forward, to process the application with the carrier. We do not sell or share your data with third parties outside that process. You may ask us to stop contacting you at any time.",
    },
  },
];
